import { INBOX_LIST_ID, type Item } from '../db/types';
import type { Catalog } from '../lib/views';
import {
  formatClock,
  formatDueCompact,
  formatDueShort,
  formatTime,
  hasTime,
  isOverdue,
  notStartedYet,
} from '../lib/dates';
import { Checkbox } from './Checkbox';
import { highlight, type Segment } from '../lib/search';
import './Row.css';

interface Props {
  item: Item;
  catalog: Catalog;
  selected?: boolean;
  /**
   * `time` — the section header already says which day, so the row shows only
   * a clock time. `full` — the row stands alone and carries the whole date.
   */
  dateStyle?: 'time' | 'full';
  /** Terms to highlight, when the row is a search result. */
  terms?: string[];
  onOpen?: () => void;
  onToggle?: () => void;
  onDelete?: () => void;
  onSchedule?: () => void;
}

export function TaskRow({
  item,
  catalog,
  selected = false,
  dateStyle = 'full',
  terms,
  onOpen,
  onToggle,
  onDelete,
  onSchedule,
}: Props) {
  const overdue = isOverdue(item.due_at, item.done);
  const pending = !item.done && notStartedYet(item.start_at);

  return (
    <div
      className={[
        'row',
        item.done && 'is-done',
        selected && 'is-selected',
        pending && 'is-pending',
      ]
        .filter(Boolean)
        .join(' ')}
      role="option"
      aria-selected={selected}
      tabIndex={-1}
      onClick={onOpen}
      onDoubleClick={onToggle}
    >
      <Checkbox
        kind="todo"
        done={item.done}
        overdue={overdue}
        pending={pending}
        onToggle={onToggle}
        label={`${item.done ? 'Reopen' : 'Complete'} ${item.title}`}
      />

      <div className="row__body">
        <div className="row__title">{terms ? <Marked text={item.title} terms={terms} /> : item.title}</div>
        <div className={`row__meta${overdue ? ' row__meta--overdue' : ''}`}>
          {terms ? (
            <Marked text={metaLine(item, catalog, dateStyle, overdue, pending)} terms={terms} />
          ) : (
            metaLine(item, catalog, dateStyle, overdue, pending)
          )}
        </div>
      </div>

      {item.priority < 4 && !item.done && (
        <span className={`pri-dot pri-dot--${item.priority} row__dot`} title={`P${item.priority}`} />
      )}

      <div className="row__actions">
        {onSchedule && (
          <button type="button" className="row__action" title="Schedule" onClick={(e) => { e.stopPropagation(); onSchedule(); }}>
            ⏱
          </button>
        )}
        {onDelete && (
          <button type="button" className="row__action" title={item.deleted ? 'Delete permanently' : 'Delete'} onClick={(e) => { e.stopPropagation(); onDelete(); }}>
            ⌫
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * The metadata line: date, then list, then tags — dropping anything the
 * surrounding context has already said.
 */
function metaLine(
  item: Item,
  catalog: Catalog,
  dateStyle: 'time' | 'full',
  overdue: boolean,
  pending: boolean,
): string {
  const parts: string[] = [];

  if (item.done && item.completed_at) {
    parts.push(`Completed ${formatClock(item.completed_at)}`);
  } else if (pending && item.start_at) {
    // Two dates on one line, so both take the narrow form.
    parts.push(
      `starts ${formatDueCompact(item.start_at)}${item.due_at ? ` · due ${formatDueCompact(item.due_at)}` : ''}`,
    );
  } else if (item.due_at) {
    // Overdue always spells out the date — "Overdue" as a heading does not say
    // how overdue, and that difference is the whole point of the colour.
    if (overdue || dateStyle === 'full') parts.push(formatDueShort(item.due_at));
    else if (hasTime(item.due_at)) parts.push(formatTime(item.due_at));
  }

  // The Inbox is where things are when nobody has said otherwise, so naming it
  // on every row would be noise rather than information.
  const list = catalog.lists.find((l) => l.id === item.list_id);
  if (list && list.id !== INBOX_LIST_ID) parts.push(list.name);

  for (const id of item.list_tag_ids) {
    const tag = catalog.listTags.find((t) => t.id === id);
    if (tag) parts.push(tag.name);
  }
  for (const id of item.global_tag_ids) {
    const tag = catalog.globalTags.find((t) => t.id === id);
    if (tag) parts.push(tag.name);
  }

  return parts.join(' · ');
}

export function Marked({ text, terms }: { text: string; terms: string[] }) {
  return (
    <>
      {highlight(text, terms).map((seg: Segment, i) =>
        seg.match ? (
          <span key={i} className="match">
            {seg.text}
          </span>
        ) : (
          <span key={i}>{seg.text}</span>
        ),
      )}
    </>
  );
}

/** Placeholder shown while the first Dexie read is in flight. */
export function TaskRowSkeleton() {
  return (
    <div className="row" aria-hidden="true">
      <span className="spinner" style={{ width: 16, height: 16 }} />
      <div className="row__skeleton">
        <span style={{ width: '62%' }} />
        <span />
      </div>
    </div>
  );
}
